import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ERROR_MSG } from "../../../../core/data/constants/constant";
import api from "../../../../utils/api";

const useReminders = (module, moduleId) => {
  const queryClient = useQueryClient();

  // ✅ Directly use `useQuery`
  const { data: reminders, isLoading } = useQuery({
    queryKey: ["reminders"],
    queryFn: async () => {
      const response = await api.get(`/functionality/get-my-reminder`);
      const reminders = response.data.data?.[0]?.reminders || []; // ✅ Correctly extract reminders array
      localStorage.setItem("reminders", JSON.stringify(reminders));
      return reminders;
    },
    staleTime: Infinity,
    refetchOnMount: false, // Prevents refetch on mount
    refetchOnWindowFocus: false, // Prevents refetch when switching tabs
  });

  // Set Reminder
  const setReminder = useMutation({
    mutationFn: async (values) => {
      // console.log(values);

      const response = await api.post(
        `/functionality/set-reminder/${module}`,
        values
      );
      return response.data;
    },
    onSuccess: (response, values) => {
      toast.success(response?.msg || "Reminder set successfully!");
      localStorage.setItem("reminders", JSON.stringify(response?.data));
      queryClient.invalidateQueries(["reminders", module, values.moduleId]); // Refresh reminders
    },
    onError: (response) => {
      toast.error(response?.msg || ERROR_MSG);
    },
  });

  // Edit Reminder
  const editReminder = useMutation({
    mutationFn: async ({ reminderId, values }) => {
      // console.log(values);

      const response = await api.patch(
        `/functionality/edit-reminder/${module}/${reminderId}`,
        values
      );
      return response.data;
    },
    onSuccess: (response, variables) => {
      toast.success(response?.msg || "Reminder updated successfully!");
      localStorage.setItem("reminders", JSON.stringify(response?.data));
      queryClient.invalidateQueries(["reminders", module, variables.moduleId]);
    },
    onError: (response) => {
      toast.error(response?.msg || ERROR_MSG);
    },
  });

  // Delete Reminder
  const deleteReminder = useMutation({
    mutationFn: async ({ moduleId, reminderId }) => {
      const response = await api.delete(
        `/functionality/delete-reminder/${reminderId}`
      );
      return response.data;
    },
    onSuccess: (response, variables) => {
      toast.success(response?.msg || "Reminder deleted successfully!");
      localStorage.removeItem("reminders", JSON.stringify(response?.data));
      queryClient.invalidateQueries(["reminders", module, variables.moduleId]); // Refresh reminders
    },
    onError: (response) => {
      toast.error(response?.msg || ERROR_MSG);
    },
  });

  return { reminders, isLoading, setReminder, editReminder, deleteReminder };
};

export default useReminders;
